<template>
	<widget title="Customer Asset Templates" icon="fa-tag">

		<search-bar
			class="mt-2 mb-4"
			style="width: 350px; max-width: 100%"
			placeholder="Search templates..."
			:full-width="true"
			v-on:submit="search">
		</search-bar>

		<categorized-list-module
			record-name="Template"
			category-api-resource="CustomerAssetTemplateCategory"
			record-api-resource="CustomerAssetTemplate"
			num-records-field="num_templates"
			:list-fields="[
				{ label: 'Template Description',  key: 'description' }
			]"
			:payload-initial-values="payloadInitialValues"
			:searchable-fields="['description']"
			:search-terms="searchTerms"
			:fetch-trigger-toggle="fetchTriggerToggle"
			:hide-record-import="true"
			:record-include="recordInclude"
			v-on:editRecord="openEditModal"
			v-on:newRecord="openEditModal(null)"
			v-on:categoryChange="categoryChange">
		</categorized-list-module>


		<modal-dialog
			:modal-visible="editModal.visible"
			:title="editModal.title"
			modal-size="modal-lg"
			confirm-button-text="Save"
			cancel-button-text="Done"
			v-on:closeModal="closeEditModal"
			v-on:confirm="saveTemplateDetails">

			<div v-if="editModal.record">

				<field
					class="mb-4"
					:params="{type:'btn-group',buttons: editModalTabButtons, disableNull: true}"
					v-model="editModal.tab">
				</field>

				<template v-if="editModal.tab == 'general'">

					<h4>General</h4>

					<field v-if="selectedCategory"
						label="Template Category"
						col-class="col-sm-3"
						:params="{ type: 'static'}"
						:value="selectedCategory.name">
					</field>

					<field
						class="mt-2"
						label="Template Description"
						col-class="col-sm-3"
						v-model="editModal.record.description">
					</field>
					
				</template>

				<template v-else-if="editModal.tab == 'parts'">

					<div class="d-flex justify-content-between align-items-baseline">

						<div class="d-flex">
							<h4 class="mr-4">Parts</h4>

							<a v-if="hasModulePermWrite('Materials')" href="/materials/list" target="_blank">
								View All
							</a>
						</div>

						<button type="button" class="btn btn-success btn-sm" v-on:click="materialSelectorModalVisible = true">
							<i class="fa fa-plus"></i>
							Add Part
						</button>

					</div>

					<table-list
						record-name="part"
						:records="editModal.record.Material"
						:fields="[
							{ label: 'Description',  key: 'description' },
							{ label: 'Part Number',  key: 'part_number', align: 'right' }
						]"
						:hide-record-count="true"
						:has-delete="true"
						v-on:delete="removeTemplateMaterial">
					</table-list>

				</template>


				<custom-fields-list
					v-else-if="editModal.tab == 'custom-fields'"
					:custom-fields="editModal.customFields"
					v-on:change="saveTemplateFields">
				</custom-fields-list>


				<template v-else-if="editModal.tab == 'checklists'">

					<div class="d-flex justify-content-between align-items-baseline">

						<div class="d-flex">
							<h4 class="mr-4">Checklists</h4>

							<a v-if="hasModulePermWrite('Settings')" href="/settings/checklists" target="_blank">
								View All
							</a>
						</div>

						<button type="button" class="btn btn-success btn-sm" v-on:click="openChecklistSelectorModal">
							<i class="fa fa-plus"></i>
							Add Checklist
						</button>

					</div>

					<p class="mt-2">
						These checklists will be used in future services for customer assets built from this template.
					</p>

					<checklist-table
						:hide-edit="true"
						:hide-delete="false"
						v-on:delete="removeTemplateChecklist"
						v-model="editModal.record.Checklist">
					</checklist-table>

				</template>


				<template v-else-if="editModal.tab == 'forms'">

					<div class="d-flex justify-content-between align-items-baseline">

						<div class="d-flex">
							<h4 class="mr-4">Form Child Templates</h4>

							<a v-if="hasModulePermWrite('Company Forms')" href="/forms/templates" target="_blank" class="mr-3">
								View All
							</a>
						</div>

						<button type="button" class="btn btn-success btn-sm" v-on:click="formSelectorModalVisible = true">
							<i class="fa fa-plus"></i>
							Add Form
						</button>

					</div>

					<p class="mt-2">
						These form child templates will be used in future services for customer assets built from this asset template.
					</p>

					<table-list
						:records="mutatedFormChildTemplates"
						:fields="[
							{ label: 'Form Category / Template', key: 'breadcrumb' },
							{ label: 'Description',              key: 'description' },
						]"
						record-name="Form Child Template"
						:hide-record-count="true"
						:sortable="true"
						:has-delete="true"
						v-on:delete="removeTemplateForm">
					</table-list>

				</template>


			</div>

			<template slot="footer">
				<button v-show="editModal.record && 'id' in editModal.record && editModal.record.id" class="btn btn-outline-danger" type="button" v-on:click="deleteTemplate">
					<i class="fa fa-trash"></i>
					Delete
				</button>
			</template>

		</modal-dialog>


		<!--- Add Material Modal --->
		<material-selector-modal
			:visible="materialSelectorModalVisible"
			:disable-manual="true"
			:disable-assemblies="true"
			:disable-price="true"
			:disable-qty="true"
			:disable-serial-num="true"
			:disable-warranty="true"
			:expenseLogPrompt="false"
			v-on:addMaterials="addTemplateMaterials"
			v-on:closeModal="materialSelectorModalVisible = false">
		</material-selector-modal>


		<!--- Add Checklist Modal --->
		<modal-dialog
			:modal-visible="checklistSelectorModal.visible"
			title="Add Checklist"
			confirm-button-text="Add"
			v-on:closeModal="checklistSelectorModal.visible = false"
			v-on:confirm="addTemplateChecklist">

			<field
				label="Checklist"
				col-class="col-sm-3"
				:params="{type: 'select', options: checklistOptions}"
				v-model="checklistSelectorModal.checklistId">
			</field>

		</modal-dialog>


		<!--- Add Form Child Template Modal --->
		<form-child-template-selector-modal
			:visible="formSelectorModalVisible"
			v-on:closeModal="formSelectorModalVisible = false"
			v-on:childTemplateSelected="addTemplateForms">
		</form-child-template-selector-modal>



		<!---- Confirm update children ---->
		<modal-dialog
			:modal-visible="childAssetsModal.visible"
			title="Update Assets?"
			modal-size="modal-lg"
			confirm-button-text="Confirm"
			v-on:closeModal="childAssetsModal.visible = false"
			v-on:confirm="saveChildAssets">

			There are some existing customer assets built from this template.

			<div class="mt-2">
				Would you like to update any assets to include these new {{tabRecordTypes[editModal.tab]}}s?
				Select which assets should be updated, and click "Confirm", if applicable.  Otherwise, you may close this window.

				<table-list
					class="mt-4"
					:records="mutatedAssets"
					:fields="[
						{ label: 'Description', key: 'description' },
						{ label: 'Serial #',    key: 'serial_num' },
						{ label: 'Customer',    key: 'customerName' },
					]"
					:hide-record-count="true"
					:records-selectable="true"
					v-on:selectedRecordsChange="setSelectedAssets">
				</table-list>

			</div>
		</modal-dialog>



		<!---- Confirm add PART to children -
		<modal-dialog
			v-if="hasChildAssets"
			:modal-visible="assetMaterialsModal.visible"
			title="Update Related Assets?"
			confirm-button-text="Confirm"
			v-on:closeModal="assetMaterialsModal.visible = false"
			v-on:confirm="saveChildAssets('materials')">

			There are ({{editModal.record.CustomerAsset.length}}) customer assets built from this template.

			<div v-if="assetMaterialsModal.materials.length === 1">
				Would you like to update all assets to include this new part?
			</div>

			<div v-else class="mt-2">
				Would you like to update the assets to include these new parts? Please select which parts you would like to
				include, and click "Confirm", if applicable.  Otherwise, you may close this window.

				<table-list
					class="mt-3"
					:records="assetMaterialsModal.materials"
					:fields="materialTableFields"
					:hide-record-count="true"
					:records-selectable="true"
					v-on:selectedRecordsChange="setSelectedMaterials">
				</table-list>

			</div>
		</modal-dialog>


		---- Confirm add CUSTOM FIELD to children ----
		<modal-dialog
			v-if="hasChildAssets"
			:modal-visible="assetFieldModal.visible"
			title="Update Related Assets?"
			confirm-button-text="Confirm"
			v-on:closeModal="assetFieldModal.visible = false"
			v-on:confirm="saveChildAssets('fields')">

			There are ({{editModal.record.CustomerAsset.length}}) customer assets built from this template.
			Would you like to update all assets to include this new custom field?

		</modal-dialog>

		--->

	</widget>
</template>
<script type="text/javascript">

import Widget from "@/components/Widget"
import SearchBar from "@/components/SearchBar"
import CategorizedListModule from "@/components/super_components/CategorizedListModule"
import ModalDialog from "@/components/ModalDialog"
import Field from "@/components/Field"
import CustomFieldsList from "@/components/super_components/CustomFieldsList"
import TableList from "@/components/TableList"
import MaterialSelectorModal from "@/components/super_components/MaterialSelectorModal"
import FormChildTemplateSelectorModal from "@/components/super_components/FormChildTemplateSelectorModal"
import ChecklistTable from "@/components/super_components/ChecklistTable"


export default {
	name: "CustomerAssetTemplatesManagerWidget",
	components: {
		FormChildTemplateSelectorModal,
		Widget,
		SearchBar,
		CategorizedListModule,
		ModalDialog,
		Field,
		CustomFieldsList,
		TableList,
		MaterialSelectorModal,
		ChecklistTable
	},
	props: [],
	data() {
		return {

			// fetched on creation
			checklists: [],           // used in "Edit Template" modal options

			// CategorizedListModule parameters
			searchTerms: "",
			payloadInitialValues: {
				description: "",
			},
			fetchTriggerToggle: false,

			// Ensure that whenever a FormChildTemplate is fetched, the required related records are also fetched
			paramsFormChildTemplate: {
				model: 'FormChildTemplate',
				fields: ['id', 'slug', 'description', 'form_template_id', 'FormTemplate'],
				include: [
					{
						model: 'FormTemplate',
						fields: ['id', 'slug', 'title', 'category_id', 'FormTemplateCategory'],
						include: [{ model:'FormTemplateCategory'}]
					}
				]
			},

			// Editing a Customer Asset Template
			editModal: {
				title: "",
				visible: false,
				tab: 'general',        // current tab
				record: {},            // the CustomerAssetTemplate we are editing
				customFields: [],      // parsed from editModal.record.custom_fields_json
				recordChanged: false,  // changed to true after we save editModal.record.  Used to trigger a re-fetching of our record
			},

			tabRecordTypes: {
				'general': null,
				'parts': 'part',
				'custom-fields': 'custom field',
				'checklists': 'checklist',
				'forms': 'form child template'
			},

			materialSelectorModalVisible: false,    // modal for adding new material to CustomerAssetTemplate
			checklistSelectorModal: {               // modal for adding new checklist to CustomerAssetTemplate
				visible: false,
				checklistId: null
			},
			formSelectorModalVisible: false,        // modal for adding new form child template to CustomerAssetTemplate


			// Modal to select which children should be updated on the creation of
			// new parts/fields/checklists/forms children
			childAssetsModal: {
				visible: false,
				assets: [],
				selectedAssets: [],
				newValues: [],         // our new value(s) to append. Might be a list of parts, new custom fields, new checklists... etc.
			},

			selectedCategory: null,

		}
	},
	computed: {
		mutatedAssets() {
			return this.childAssetsModal.assets.map(asset => {

				asset.customerName = ""
				if ('Contact' in asset && asset.Contact && 'name' in asset.Contact && asset.Contact.name)
					asset.customerName = asset.Contact.name

				return asset
			})
		},
		mutatedFormChildTemplates() {
			if (!(this.editModal.record && 'FormChildTemplate' in this.editModal.record))
				return []

			return this.editModal.record.FormChildTemplate.map(childTemplate => {

				let breadcrumbTokens = []

				if ('FormTemplate' in childTemplate && childTemplate.FormTemplate) {

					const template = childTemplate.FormTemplate   // alias

					if ('FormTemplateCategory' in template && template.FormTemplateCategory &&
						'name' in template.FormTemplateCategory && template.FormTemplateCategory.name)
					{
						breadcrumbTokens.push(template.FormTemplateCategory.name)
					}

					if ('title' in template && template.title)
						breadcrumbTokens.push(template.title)
				}

				childTemplate.breadcrumb = breadcrumbTokens.join(" / ")
				return childTemplate
			})
		},
		checklistOptions() {
			return this.optionify(this.checklists, 'name')
		},
		/*
		categoryOptions() {
			if (!this.$store.materialCategories)
				return []
			
			let mutatedCats = this.$store.materialCategories.map(cat => {
				cat.nameWithParent = cat.name
				if (cat.ParentCategory)
					cat.nameWithParent += " (in "+cat.ParentCategory.name+")"
				return cat
			})
			return this.optionify(mutatedCats,"nameWithParent")
		},
		*/

		editModalTabButtons() {
			// New records only have access to the "General" tab
			if (!(this.editModal.record && 'id' in this.editModal.record && this.editModal.record.id))
				return [{text: 'General', value:'general'}]

			// Existing records have access to all tabs
			return [
				{text: 'General',       value:'general'},
				{text: 'Parts',         value:'parts'},
				{text: 'Custom Fields', value:'custom-fields'},
				{text: 'Checklists',    value:'checklists'},
				{text: 'Forms',         value:'forms'}
			]
		},

		recordInclude() {
			return [
				{
					model: 'Material',
					fields: ['id', 'slug', 'description', 'part_number']
				},
				{
					model: 'FormChildTemplate',
					fields: ['id', 'slug', 'description', 'form_template_id', 'FormTemplate'],
					include: [
						{
							model: 'FormTemplate',
							fields: ['id', 'slug', 'title', 'category_id', 'FormTemplateCategory'],
							include: [{ model:'FormTemplateCategory'}]
						}
					]
				},
				'Checklist',
			]
		}
	},
	methods: {

		saveChildAssets() {
			const vm = this

			console.log(this.cloneObj(this.childAssetsModal.selectedAssets))
			console.log(this.cloneObj(this.childAssetsModal.newValues))
			console.log('payload', this.cloneObj(this.assetPartsPayload()))
			console.log('payload', this.cloneObj(JSON.parse(this.assetPartsPayload().records)))
			console.log('payload', this.cloneObj(JSON.parse(JSON.parse(this.assetPartsPayload().records)[0].parts_json)))

			/*
			let payload = null

			if (this.editModal.tab == 'parts')
				payload = this.assetMaterialsPayload()
			else if (this.editModal.tab == 'custom-fields')
				payload = this.assetFieldsPayload()

			if (!payload)
				return

			this.postData('CustomerAsset/save-batch', payload).then(response => {
				if (response.success) {
					vm.assetMaterialsModal.visible = false
					vm.assetFieldModal.visible = false
					vm.alertSuccess("Customer assets saved")
				}
			})

			 */

		},
		assetFieldsPayload() {
			const records = this.editModal.record.CustomerAsset.map(asset => {

				let fieldsJSON = asset.custom_fields_json

				try {
					const fields = (fieldsJSON && fieldsJSON != "{}") ? JSON.parse(fieldsJSON) : []
					fields.push(this.assetFieldModal.field)
					fieldsJSON = JSON.stringify(fields)

				} catch (e){
					console.log(e)
				}

				return {
					id: asset.id,
					custom_fields_json: fieldsJSON
				}
			})

			return {
				records: JSON.stringify(records)
			}
		},
		assetPartsPayload() {

			const records = this.childAssetsModal.selectedAssets.map(asset => {

				let partsJSON = asset.parts_json
				const partKeys = ['id', 'description', 'part_number']

				try {

					const assetParts = (partsJSON) ? JSON.parse(partsJSON) : []

					this.childAssetsModal.newValues.forEach(newPart => {

						let newPartClean = {}
						partKeys.forEach(key => {
							if (key in newPartClean)
								newPartClean[key] = newPart[key]
						})

						// Kill the twin and keep the newer copy
						const existingPartIndex = assetParts.findIndex(p => 'id' in p && p.id == newPartClean.id)
						if (existingPartIndex > -1)
							assetParts.splice(existingPartIndex, 1)

						assetParts.push(newPartClean)
					})

					partsJSON = JSON.stringify(assetParts)

				} catch (e){
					console.log(e)
				}

				return {
					id: asset.id,
					parts_json: partsJSON
				}
			})

			return {
				records: JSON.stringify(records)
			}
		},

		setSelectedAssets(assets) {
			this.childAssetsModal.selectedAssets = assets
		},

		checkChildAssets(newValues) {
			/** Returns a promise that resolves after the new modal is opened */
			return new Promise(resolve => {
				this.fetchCustomerAssets(this.editModal.record.id).then(assets => {
					if (assets.length) {
						this.childAssetsModal.newValues = newValues
						this.childAssetsModal.assets = assets
						this.childAssetsModal.visible = true
					}
					resolve()
				})
			})
		},
		removeTemplateForm(childTemplate) {
			const vm = this
			const newChildTemplates = this.editModal.record.FormChildTemplate.filter(ct => ct.id != childTemplate.id)

			const payload = {
				id: vm.editModal.record.id,
				FormChildTemplate: newChildTemplates.map(ct => ct.id)
			}

			this.saveTemplate(payload).then(response => {
				if (response.success) {
					vm.alertSuccess("Removed form")
					vm.editModal.record.FormChildTemplate = newChildTemplates
				}
			})
		},
		addTemplateForms(newChildTemplate) {
			const vm = this

			let oldFormChildTemplates = []

			// Add existing materials
			if ('FormChildTemplate' in this.editModal.record && this.editModal.record.FormChildTemplate)
				oldFormChildTemplates = this.cloneObj(this.editModal.record.FormChildTemplate)

			const existingMatchIndex = oldFormChildTemplates.findIndex(childTemplate => childTemplate.id == newChildTemplate.id)
			if (existingMatchIndex > -1)
				oldFormChildTemplates.splice(existingMatchIndex, 1)

			// Add new materials
			const allFormChildTemplates = [
				...oldFormChildTemplates,
				newChildTemplate
			]

			let payload = {
				id: this.editModal.record.id,
				FormChildTemplate: allFormChildTemplates.map(childTemplate => childTemplate.id)
			}

			this.saveTemplate(payload).then(response => {
				if (response.success) {

					let params = this.cloneObj(this.paramsFormChildTemplate)
					delete params.model   // only keep in "fields" and "includes"

					vm.getJSON("FormChildTemplate/find/" + newChildTemplate.id, params).then(findResponse => {
						if (findResponse.success) {
							vm.editModal.record.FormChildTemplate = oldFormChildTemplates
							vm.editModal.record.FormChildTemplate.push(findResponse.data)
						}

						vm.formSelectorModalVisible = false
						vm.alertSuccess("Added form to template")
					})
				}
			})

		},

		removeTemplateChecklist(checklist) {
			const vm = this
			const newChecklists = this.editModal.record.Checklist.filter(cl => cl.id != checklist.id)

			const payload = {
				id: vm.editModal.record.id,
				Checklist: newChecklists.map(cl => cl.id)
			}

			this.saveTemplate(payload).then(response => {
				if (response.success) {
					vm.alertSuccess("Removed checklist")
					vm.editModal.record.Checklist = newChecklists
				}
			})
		},
		addTemplateChecklist() {
			const vm = this

			const newChecklist = this.checklists.find(checklist => checklist.id == this.checklistSelectorModal.checklistId)

			let allChecklists = []

			// Add existing materials
			if ('Checklist' in this.editModal.record && this.editModal.record.Checklist)
				allChecklists = this.cloneObj(this.editModal.record.Checklist)

			const existingMatchIndex = allChecklists.findIndex(checklist => checklist.id == newChecklist.id)
			if (existingMatchIndex > -1)
				allChecklists.splice(existingMatchIndex, 1)

			// Add new materials
			allChecklists = [
				...allChecklists,
				newChecklist
			]

			let payload = {
				id: this.editModal.record.id,
				Checklist: allChecklists.map(checklist => checklist.id)
			}

			this.saveTemplate(payload).then(response => {
				if (response.success) {
					vm.checklistSelectorModal.visible = false
					vm.editModal.record.Checklist = allChecklists
					vm.alertSuccess("Added checklist to template")
				}
			})
		},
		openChecklistSelectorModal() {
			/** Open the checklist selector modal */
			this.checklistSelectorModal.visible = true
			this.checklistSelectorModal.checklistId = null
		},

		saveTemplateFields(updatedFields) {
			const vm = this

			const fieldsJSON = JSON.stringify(updatedFields)

			const payload = {
				id: this.editModal.record.id,
				custom_fields_json: fieldsJSON
			}

			this.saveTemplate(payload).then(response => {
				if (response.success) {
					vm.editModal.record.custom_fields_json = fieldsJSON

					// We may add newly created fields to existing child templates
					// Note that this event is only triggered on the creation/deletion of a single asset field,
					// so we will have AT MOST only one new field when this method is triggered
					/*
					if (vm.hasChildAssets && vm.editModal.customFields.length < updatedFields.length) {
						vm.assetFieldModal.field = updatedFields[updatedFields.length - 1]
						vm.assetFieldModal.visible = true
					}
					 */

					vm.editModal.customFields = updatedFields
					vm.alertSuccess("Custom field was saved to template")
				}
			})

		},

		removeTemplateMaterial(mat) {
			const vm = this

			const newMaterials = this.editModal.record.Material.filter(m => m.id != mat.id)

			const payload = {
				id: vm.editModal.record.id,
				Material: newMaterials.map(m => m.id)
			}

			this.saveTemplate(payload).then(response => {
				if (response.success) {
					vm.alertSuccess("Removed part")
					vm.editModal.record.Material = newMaterials
				}
			})
		},
		addTemplateMaterials(mats) {
			const vm = this
			
			if (!mats.length)
				return

			let allMaterials = []

			// Add existing materials
			if ('Material' in this.editModal.record && this.editModal.record.Material)
				allMaterials = this.cloneObj(this.editModal.record.Material)

			mats.forEach(newMat => {
				const existingMatchIndex = allMaterials.findIndex(mat => mat.id == newMat.id)
				if (existingMatchIndex > -1)
					allMaterials.splice(existingMatchIndex, 1)
			})

			// Add new materials
			allMaterials = [
				...allMaterials,
				...mats
			]

			let payload = {
				id: this.editModal.record.id,
				Material: allMaterials.map(mat => mat.id)
			}

			vm.saveTemplate(payload).then(response => {
				if (response.success) {

					/*
					if (vm.hasChildAssets) {
						vm.assetMaterialsModal.materials = mats
						vm.assetMaterialsModal.visible = true
					}
					 */

					// Check children before closing modal
					vm.checkChildAssets(mats).then(() => {
						vm.materialSelectorModalVisible = false
						vm.editModal.record.Material = allMaterials
						vm.alertSuccess("Added part(s) to template")
					})
				}
			})
		},

		saveTemplateDetails() {
			const vm = this

			this.$nextTick(() => {

				let payload = {
					description: vm.editModal.record.description,
					category_id: vm.editModal.record.category_id
				}

				if ('id' in vm.editModal.record && vm.editModal.record.id)
					payload.id = vm.editModal.record.id

				this.saveTemplate(payload).then(response => {
					if (response.success) {

						if (!('id' in payload)) {
							const fetchPayload = {include: vm.recordInclude}
							vm.getJSON('CustomerAssetTemplate/find/' + response.record_id, fetchPayload).then(fetchResponse => {
								if (fetchResponse.success)
									vm.editModal.record = fetchResponse.data
							})
						}

						vm.alertSuccess('Template saved')
					}


				})
			})
		},

		saveTemplate(payload) {
			const vm = this

			return new Promise(resolve => {

				vm.postData('CustomerAssetTemplate/save', payload).then(response => {
					if (!response.success && 'errors' in response) {
						vm.alertErrors(response.errors)
					}
					vm.editModal.recordChanged = true
					resolve(response)
				})

			})
		},
		deleteTemplate() {
			let vm = this

			this.postData("CustomerAssetTemplate/delete",{slug: this.editModal.record.slug}).then(response => {
				if (response.success) {
					vm.editModal.recordChanged = true
					vm.closeEditModal()
				}
			})
		},
		closeEditModal() {
			if (this.editModal.recordChanged)
				this.fetchTriggerToggle = !this.fetchTriggerToggle

			this.editModal.visible = false
		},
		openEditModal(record) {

			this.editModal.visible = true
			this.editModal.tab = 'general'
			this.editModal.customFields = []
			this.editModal.recordChanged = false

			if (record) {
				this.editModal.record = record
				this.editModal.title = "Edit Asset Template"

				try {
					this.editModal.customFields = JSON.parse(this.editModal.record.custom_fields_json)
				} catch (e) {}

			} else {
				this.editModal.record = {
					category_id: this.selectedCategory.id,
					description: ""
				}
				this.editModal.title = "New Asset Template"
			}
		},

		categoryChange(cat) {
			/** For CategorizedListModule - set current category on change */
			this.selectedCategory = cat
		},
		search(terms) {
			/** For CategorizedListModule - set search terms from SearchBar */
			this.searchTerms = terms
		},

		fetchChecklists() {
			/** Fetch checklists for "Edit Modal" -> "Add Checklist" */
			const vm = this

			this.getJSON("Checklist/get").then(response => {
				if (response.success)
					vm.checklists = response.data
			})
		},

		fetchCustomerAssets(templateId) {

			let params = {
				fields: ['id', 'slug', 'description', 'serial_num', 'Contact'],
				filters: [['template_id', templateId]],
				include: [
					{ model: 'Contact', fields: ['name'] }
				]
			}

			if (this.editModal.tab == 'parts')
				params.fields.push('parts_json')

			else if (this.editModal.tab == 'custom-fields')
				params.fields.push('required_fields_json')

			else if (this.editModal.tab == 'checklists') {
				params.fields.push('Checklist')
				params.include.push({
					model: 'Checklist',
					fields: ['id']
				})
			}

			else if (this.editModal.tab == 'forms') {
				params.fields.push('FormChildTemplate')
				params.include.push({
					model: 'FormChildTemplate',
					fields: ['id']
				})
			}

			return new Promise(resolve => {
				this.getJSON('CustomerAsset/get', params).then(response => {
					if (response.success)
						resolve(response.data)
					resolve([])
				})
			})
		}
	},
	created() {
		this.fetchChecklists()
	}
}
</script>

